import { Analytics } from '@analytics';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React from 'react';
import { useIntl } from 'react-intl';
import * as yup from 'yup';
import CustomSection from '../CustomSection';
import messages from './messages';

// Mirrors MAX_SHARE_TITLE / MAX_SHARE_TEXT in TA-Ticketing
// (backend/src/domain/tap_to_tip/tour_photos/model/index.ts). The server trims
// to these too — this is so the operator finds out while typing rather than by
// seeing their words cut off in a Facebook preview.
export const MAX_SHARE_TITLE = 70;
export const MAX_SHARE_TEXT = 200;

const useStyles = makeStyles({
  textField: {
    width: '100%',
    fontSize: '14px',
    color: '#000',
    fontWeight: 500,

    '& .MuiFormHelperText-root': {
      backgroundColor: 'transparent'
    },

    '& .MuiInputBase-root': {
      backgroundColor: '#fff',
      color: '#000',
      fontWeight: 500
    }
  },
  note: {
    fontSize: '13px',
    lineHeight: 1.5,
    color: '#6e6f6f'
  },
  preview: {
    fontSize: '13px',
    lineHeight: 1.5,
    color: '#0B0F1F',
    backgroundColor: '#f6f7fb',
    border: '1px solid #e0e4ef',
    borderRadius: '8px',
    padding: '12px 14px'
  },
  previewLabel: {
    fontSize: '11px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#8a8a8a',
    marginBottom: '4px'
  }
});

/**
 * Card 289 — the operator's own wording for a shared tour photo.
 *
 * One set of copy drives two surfaces: the share sheet a guest sees when they
 * tap Share, and the Open Graph tags Facebook and LinkedIn read off the share
 * page. They are saved together so the two can never disagree.
 *
 * Only rendered when the company has tour photos switched on — there is nothing
 * to configure otherwise, and the feature is invisible to them until then.
 */
const shareCopySchema = yup.object({
  shareTitle: yup.string().max(MAX_SHARE_TITLE),
  shareText: yup.string().max(MAX_SHARE_TEXT)
});

// Same substitution the server does, so the preview is honest about what a
// guest will actually read.
const applyTokens = (template, { companyName, guideName }) =>
  (template || '')
    .split('{company}')
    .join(companyName || '')
    .split('{guide}')
    .join(guideName || '')
    .replace(/\s+([.,!?])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();

const TourPhotoShareSection = ({
  shareTitle,
  shareText,
  companyName,
  onSave
}) => {
  const intl = useIntl();
  const classes = useStyles();

  const handleSave = values => {
    Analytics.track('Shared Photo Message saved', { page: 'setup' });
    onSave({
      shareTitle: values.shareTitle,
      shareText: values.shareText
    });
  };

  const renderSectionBody = ({ values, errors, touched, handleChange, handleBlur }) => {
    // The guide differs per photo, so the preview stands in a sample name
    // rather than pretending to know which guide it will be.
    const previewText = applyTokens(
      values.shareText || intl.formatMessage(messages.shareTextPlaceholder),
      { companyName, guideName: intl.formatMessage(messages.sampleGuideName) }
    );

    return (
      <div className="flex flex-col gap-5 w-full">
        <TextField
          type="text"
          name="shareTitle"
          className={classes.textField}
          label={intl.formatMessage(messages.shareTitle)}
          variant="outlined"
          placeholder={intl.formatMessage(messages.shareTitlePlaceholder)}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.shareTitle}
          inputProps={{ maxLength: MAX_SHARE_TITLE }}
          error={touched?.shareTitle && Boolean(errors?.shareTitle)}
          helperText={
            (touched?.shareTitle && errors?.shareTitle) ||
            `${(values.shareTitle || '').length}/${MAX_SHARE_TITLE}`
          }
        />
        <TextField
          multiline
          minRows={3}
          type="text"
          name="shareText"
          className={classes.textField}
          label={intl.formatMessage(messages.shareText)}
          variant="outlined"
          placeholder={intl.formatMessage(messages.shareTextPlaceholder)}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.shareText}
          inputProps={{ maxLength: MAX_SHARE_TEXT }}
          error={touched?.shareText && Boolean(errors?.shareText)}
          helperText={
            (touched?.shareText && errors?.shareText) ||
            `${(values.shareText || '').length}/${MAX_SHARE_TEXT}`
          }
        />

        <div>
          <div className={classes.previewLabel}>
            {intl.formatMessage(messages.preview)}
          </div>
          <div className={classes.preview} data-testid="share-copy-preview">
            {previewText}
          </div>
        </div>

        <div className={classes.note}>
          {intl.formatMessage(messages.instagramNote)}
        </div>
      </div>
    );
  };

  return (
    <CustomSection
      id="TourPhotoShare"
      title={intl.formatMessage(messages.title)}
      description={intl.formatMessage(messages.description)}
      renderBody={renderSectionBody}
      buttonTitle={intl.formatMessage(messages.save)}
      onSave={handleSave}
      initialValues={{
        shareTitle: shareTitle || '',
        shareText: shareText || ''
      }}
      schema={shareCopySchema}
    />
  );
};

export default TourPhotoShareSection;
